import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import InfoButton from './InfoButton';
import { fieldMissionDeviceInfo } from './operationsInfo';
import {
  getFieldMissionDevices,
  getDeviceCatalogEntries,
  createFieldMissionDevice,
  deleteFieldMissionDevice,
} from '../../services/adminService';
import { FieldMissionDevice, CreateFieldMissionDevicePayload, DeviceCatalogEntry } from '../../types/device';
import {
  AdminCard,
  AdminCardHeader,
  AdminTable,
  AdminTableRow,
  AdminPrimaryButton,
  AdminDangerButton,
  AdminForm,
  AdminFormGroup,
  AdminFormLabel,
  AdminFormInput,
  AdminFormSelect,
  AdminErrorMessage,
  AdminSuccessMessage,
  AdminLoadingMessage,
  AdminFormActions,
  AdminEmptyState,
  adminColors,
} from '../../styles/adminStyles';
import ConfirmModal from '../common/ConfirmModal';
import { useConfirmModal } from '../../hooks/useConfirmModal';

// Table specific styles
const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 140px 220px 120px 140px 140px 100px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 164, 255, 0.05) 100%);
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: ${adminColors.secondary};
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 14px 20px;
  border-bottom: 1px solid ${adminColors.borderLight};

  @media (max-width: 1200px) {
    grid-template-columns: 100px 180px 100px 120px 120px 80px;
    font-size: 10px;
    padding: 12px 16px;
  }
`;

const TableRow = styled(AdminTableRow)`
  grid-template-columns: 140px 220px 120px 140px 140px 100px;

  @media (max-width: 1200px) {
    grid-template-columns: 100px 180px 100px 120px 120px 80px;
  }
`;

const GuidCell = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: ${adminColors.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterLabel = styled.span`
  font-size: 12px;
  color: ${adminColors.textSecondary};
  text-transform: uppercase;
`;

const FilterSelect = styled.select`
  background: ${adminColors.surface};
  border: 1px solid ${adminColors.borderLight};
  color: ${adminColors.text};
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${adminColors.secondary};
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding: 16px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? adminColors.secondary : 'transparent'};
  color: ${props => props.$active ? '#000' : adminColors.text};
  border: 1px solid ${props => props.$active ? adminColors.secondary : adminColors.borderLight};
  padding: 8px 14px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: ${adminColors.secondary};
    color: ${adminColors.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  color: ${adminColors.textSecondary};
  font-size: 13px;
`;

const DeviceIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  margin-right: 8px;
  vertical-align: middle;
`;

const PAGE_SIZE = 10;

const FieldMissionDeviceManager: React.FC = () => {
  const { confirm, modalProps } = useConfirmModal();
  const [devices, setDevices] = useState<FieldMissionDevice[]>([]);
  const [catalogEntries, setCatalogEntries] = useState<DeviceCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Pagination and filter state
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'assigned' | 'unassigned'>('unassigned');

  // Form state
  const [formData, setFormData] = useState<CreateFieldMissionDevicePayload>({
    dcId: '',
    deviceCode: '',
    deviceType: '',
  });

  useEffect(() => {
    loadData();
  }, [page, assignmentFilter]);

  useEffect(() => {
    // Load catalog entries once on mount
    loadCatalogEntries();
  }, []);

  const loadCatalogEntries = async () => {
    try {
      const catalogResponse = await getDeviceCatalogEntries(false);
      setCatalogEntries(catalogResponse);
    } catch (err) {
      console.error('Failed to load catalog entries', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filterValue = assignmentFilter === 'all' ? undefined : assignmentFilter;
      const devicesResponse = await getFieldMissionDevices(page, PAGE_SIZE, filterValue);
      setDevices(devicesResponse.records);
      setTotalCount(devicesResponse.totalCount);
    } catch (err) {
      setError('Failed to load field mission devices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    const firstCatalog = catalogEntries.length > 0 ? catalogEntries[0] : null;
    setFormData({
      dcId: firstCatalog?.id || '',
      deviceCode: '',
      deviceType: firstCatalog?.deviceType || '',
    });
    setError(null);
    setSuccess(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'dcId') {
      // When catalog changes, update deviceType too
      const selectedCatalog = catalogEntries.find(c => c.id === value);
      setFormData(prev => ({
        ...prev,
        dcId: value,
        deviceType: selectedCatalog?.deviceType || '',
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.dcId) {
      setError('Please select a device type');
      return;
    }
    if (!formData.deviceCode.trim()) {
      setError('Please enter a device code');
      return;
    }

    try {
      await createFieldMissionDevice(formData);
      setSuccess('Field mission device created successfully');
      setIsModalOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to create field mission device');
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Device',
      message: 'Are you sure you want to delete this field mission device?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteFieldMissionDevice(id);
      setSuccess('Device deleted successfully');
      loadData();
    } catch (err) {
      setError('Failed to delete device');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAssignmentFilter(e.target.value as 'all' | 'assigned' | 'unassigned');
    setPage(1); // Reset to first page when filter changes
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <>
      <Header>
        <Actions>
          <AdminPrimaryButton onClick={handleOpenModal}>
            + Add Field Mission Device
          </AdminPrimaryButton>
          <InfoButton title="Field Mission Device Help" sections={fieldMissionDeviceInfo} />
        </Actions>
        <FilterContainer>
          <FilterLabel>Status:</FilterLabel>
          <FilterSelect value={assignmentFilter} onChange={handleFilterChange}>
            <option value="all">All</option>
            <option value="assigned">Assigned (has OFC)</option>
            <option value="unassigned">Unassigned (free)</option>
          </FilterSelect>
        </FilterContainer>
      </Header>

      {error && <AdminErrorMessage>{error}</AdminErrorMessage>}
      {success && <AdminSuccessMessage>{success}</AdminSuccessMessage>}
      {loading && <AdminLoadingMessage>Loading field mission devices...</AdminLoadingMessage>}

      <AdminCard>
        <AdminCardHeader>Field Mission Devices ({totalCount})</AdminCardHeader>
        <AdminTable>
          <TableHeader>
            <span>Device Code</span>
            <span>ID (GUID)</span>
            <span>Device Type</span>
            <span>Model</span>
            <span>Created</span>
            <span>Actions</span>
          </TableHeader>

          {devices.length === 0 && !loading ? (
            <AdminEmptyState>No field mission devices</AdminEmptyState>
          ) : (
            devices.map((device) => (
              <TableRow key={device.id}>
                <span>
                  {device.iconUrl && <DeviceIcon src={device.iconUrl} alt={device.deviceType} />}
                  {device.deviceCode}
                </span>
                <GuidCell title={device.id}>{device.id}</GuidCell>
                <span>{device.deviceType}</span>
                <span>{device.model}</span>
                <span>{formatDate(device.createdAt)}</span>
                <span>
                  <AdminDangerButton onClick={() => handleDelete(device.id)}>
                    Delete
                  </AdminDangerButton>
                </span>
              </TableRow>
            ))
          )}
        </AdminTable>

        {totalPages > 1 && (
          <Pagination>
            <PageButton
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              ← Prev
            </PageButton>
            <PageInfo>
              Page {page} of {totalPages}
            </PageInfo>
            <PageButton
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next →
            </PageButton>
          </Pagination>
        )}
      </AdminCard>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Add Field Mission Device"
      >
        <AdminForm onSubmit={handleSubmit}>
          {error && <AdminErrorMessage>{error}</AdminErrorMessage>}

          <AdminFormGroup>
            <AdminFormLabel>Device Type (from Catalog) *</AdminFormLabel>
            <AdminFormSelect
              name="dcId"
              value={formData.dcId}
              onChange={handleInputChange}
              required
            >
              <option value="">Select device type...</option>
              {catalogEntries.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.deviceType} - {entry.model}
                </option>
              ))}
            </AdminFormSelect>
          </AdminFormGroup>

          <AdminFormGroup>
            <AdminFormLabel>Device Code *</AdminFormLabel>
            <AdminFormInput
              type="text"
              name="deviceCode"
              value={formData.deviceCode}
              onChange={handleInputChange}
              placeholder="Unique code for the device (printed on device)"
              required
            />
          </AdminFormGroup>

          <AdminFormActions>
            <AdminPrimaryButton type="submit">Create Device</AdminPrimaryButton>
          </AdminFormActions>
        </AdminForm>
      </Modal>
      <ConfirmModal {...modalProps} />
    </>
  );
};

export default FieldMissionDeviceManager;
